import { EventBus }     from '../core/EventBus.js';
import { GameState }    from '../core/GameState.js';
import { serverIntent } from '../core/serverIntent.js';
import { formatMoney, formatDate } from '../utils/formatting.js';

// Preset multipliers for the dev-only speed control — deliberately
// discrete rather than a continuous slider given the huge dynamic range
// (1x real-time up to the 200,000x server-side clamp in server/transport/
// runtime.js's setTimeScale()); a handful of presets is far more usable
// than dragging a linear slider across five orders of magnitude.
const DEV_SPEED_PRESETS = [1, 600, 6000, 60000, 200000];

/**
 * The always-visible header bar: date, live indicator, money, quick stats.
 *
 * Phase 6: speed controls and pause button removed (decision 5 — no pause in
 * the shared world). A "live" indicator replaces them to communicate
 * connection status. The `engine` parameter is accepted but ignored so callers
 * that still pass it don't break.
 *
 * Later addition: a dev-only pause + speed-slider control cluster
 * (`#hud-dev-controls`) was added back — gated on GameState.devControlsEnabled
 * (from the WS snapshot's devControlsEnabled field, itself gated server-side
 * on GAME_ENV=dev, see server/transport/runtime.js's isDevEnvironment() and
 * intents/dev.js). This is NOT the Phase 6 per-player pause the comment
 * above refers to — it pauses/re-speeds the entire shared world for every
 * connected player, and only exists at all in the dev deployment.
 */
export class HUD {
  constructor(engine) {
    // engine param retained for backward-compat call sites; ignored.
    this.$hud   = document.getElementById('hud');
    this._render();
    this._bindEvents();
  }

  _render() {
    this.$hud.innerHTML = `
      <div class="hud-left">
        <div class="hud-date" id="hud-date">—</div>
        <div class="hud-live-indicator" id="hud-live-indicator">
          <span class="live-dot" id="hud-live-dot"></span>
          <span class="live-label" id="hud-live-label">Connecting…</span>
        </div>
        <div class="hud-dev-controls" id="hud-dev-controls" style="display:none">
          <button class="hud-dev-pause-btn" id="hud-dev-pause-btn" title="Pause the world clock (dev only)">&#9208;</button>
          <div class="hud-speed" id="hud-dev-speed">
            ${DEV_SPEED_PRESETS.map(scale => `
              <button class="speed-btn" data-scale="${scale}" title="${scale.toLocaleString()}x">${scale >= 1000 ? (scale / 1000) + 'k' : scale}x</button>
            `).join('')}
          </div>
        </div>
      </div>
      <div class="hud-center">
        <span class="hud-company" id="hud-company">SeaTrader</span>
      </div>
      <div class="hud-right">
        <div class="hud-money" id="hud-money">$0</div>
        <div class="hud-stats" id="hud-stats">
          <span>Ships: 0</span>
          <span>Routes: 0</span>
          <span>Contracts: 0</span>
        </div>
        <button class="hud-feedback-btn" id="hud-feedback-btn">Feedback</button>
        <button class="hud-admin-events-btn" id="hud-admin-events-btn" style="display:none">Events</button>
        <button class="hud-admin-events-btn" id="hud-admin-players-btn" style="display:none">Players</button>
        <button class="hud-dev-tools-btn" id="hud-dev-tools-btn" style="display:none">Dev Tools</button>
        <button class="hud-menu-btn" id="hud-menu-btn">&#9776; Menu</button>
      </div>`;
  }

  _bindEvents() {
    // Feedback button
    document.getElementById('hud-feedback-btn').addEventListener('click', () => {
      EventBus.emit('ui:feedbackOpen');
    });

    // Menu button
    document.getElementById('hud-menu-btn').addEventListener('click', () => {
      EventBus.emit('ui:menuOpened');
    });

    // Admin events button (hidden unless setAdminEventsVisible(true) is called)
    document.getElementById('hud-admin-events-btn').addEventListener('click', () => {
      EventBus.emit('ui:adminEventsOpen');
    });
    document.getElementById('hud-admin-players-btn').addEventListener('click', () => {
      EventBus.emit('ui:adminPlayersOpen');
    });

    // Dev tools button — visibility driven by _updateDevControls() below,
    // same GameState.devControlsEnabled gate as the pause/speed cluster.
    document.getElementById('hud-dev-tools-btn').addEventListener('click', () => {
      EventBus.emit('ui:devToolsOpen');
    });

    // Dev-only pause/speed controls — hidden unless GameState.devControlsEnabled
    // (see _updateDevControls()). Clicking optimistically does nothing to
    // local state; the server broadcasts dev:controlsChanged back to every
    // connected client (including this one) once the intent succeeds, and
    // that's what actually updates the button states below.
    document.getElementById('hud-dev-pause-btn').addEventListener('click', () => {
      serverIntent(GameState.paused ? 'dev/resume' : 'dev/pause', {});
    });
    document.getElementById('hud-dev-speed').addEventListener('click', (e) => {
      const btn = e.target.closest('.speed-btn');
      if (!btn) return;
      serverIntent('dev/timescale', { timeScale: Number(btn.dataset.scale) });
    });

    // Live updates driven by server deltas. time:dayPassed is the real
    // server-pushed date change; time:tick is a LOCAL-only event (main.js's
    // _tickClock()) that extrapolates the displayed clock forward between
    // real pushes — without it, the date/time shown here only ever advances
    // once per real day (at 1x speed) and otherwise sits frozen, even
    // though the actual world clock keeps ticking correctly underneath.
    EventBus.on('time:dayPassed',       () => this._updateDate());
    EventBus.on('time:tick',            () => this._updateDate());
    EventBus.on('player:companyNameChanged', () => this._updateCompanyName());
    EventBus.on('player:moneyChanged',  () => this._updateMoney());
    EventBus.on('ship:purchased',       () => this._updateStats());
    EventBus.on('ship:sold',            () => this._updateStats());
    EventBus.on('ship:arrived',         () => this._updateStats());
    EventBus.on('ship:departed',        () => this._updateStats());
    EventBus.on('contracts:updated',    () => this._updateStats());
    EventBus.on('dev:controlsChanged',  () => this._updateDevControls());
    // game:loaded fires after hydrateFromSnapshot completes
    EventBus.on('game:loaded',          () => this.refresh());
  }

  _updateDate() {
    const el = document.getElementById('hud-date');
    if (el) el.textContent = formatDate(GameState.currentDate);
  }

  _updateMoney() {
    const el = document.getElementById('hud-money');
    if (el) el.textContent = formatMoney(GameState.player?.money || 0);
  }

  _updateStats() {
    const ships   = GameState.getAllShips();
    const sailing = ships.filter(s => s.status === 'sailing').length;
    const active  = GameState.contracts.active.length;
    const el = document.getElementById('hud-stats');
    if (el) el.innerHTML =
      `<span>Ships: ${ships.length}</span><span>Sailing: ${sailing}</span><span>Contracts: ${active}</span>`;
  }

  /**
   * Update the live indicator dot and label.
   * @param {'live'|'reconnecting'|'offline'} state
   */
  setLiveState(state) {
    const dot   = document.getElementById('hud-live-dot');
    const label = document.getElementById('hud-live-label');
    if (!dot || !label) return;

    dot.className = `live-dot live-${state}`;

    switch (state) {
      case 'live':
        label.textContent = 'Live';
        label.title = 'Connected to server — world is running live';
        break;
      case 'reconnecting':
        label.textContent = 'Reconnecting…';
        label.title = 'Connection lost — attempting to reconnect';
        break;
      case 'offline':
        label.textContent = 'Offline';
        label.title = 'Cannot reach server';
        break;
      default:
        label.textContent = 'Connecting…';
    }
  }

  /**
   * Show or hide the admin "Events" HUD button — gated on the account's
   * isAdmin flag (see ServerConnection.isAdmin()). Not a security boundary,
   * just visibility; the server re-checks isAdmin on every /api/admin/* call.
   * @param {boolean} visible
   */
  setAdminEventsVisible(visible) {
    for (const id of ['hud-admin-events-btn', 'hud-admin-players-btn']) {
      const btn = document.getElementById(id);
      if (btn) btn.style.display = visible ? '' : 'none';
    }
  }

  /**
   * Show/hide the dev-only pause+speed cluster and sync its button states
   * to GameState.paused/timeScale/devControlsEnabled. Called on initial
   * hydration (refresh()) and every dev:controlsChanged delta — including
   * ones triggered by a *different* connected client, since pause/speed
   * are shared world state, not per-player.
   */
  _updateDevControls() {
    const wrap = document.getElementById('hud-dev-controls');
    if (wrap) wrap.style.display = GameState.devControlsEnabled ? '' : 'none';
    const toolsBtn = document.getElementById('hud-dev-tools-btn');
    if (toolsBtn) toolsBtn.style.display = GameState.devControlsEnabled ? '' : 'none';
    if (!GameState.devControlsEnabled) return;

    const pauseBtn = document.getElementById('hud-dev-pause-btn');
    if (pauseBtn) {
      pauseBtn.innerHTML = GameState.paused ? '&#9654;' : '&#9208;';
      pauseBtn.title = GameState.paused
        ? 'Resume the world clock (dev only)'
        : 'Pause the world clock (dev only)';
      pauseBtn.classList.toggle('btn-active', GameState.paused);
    }

    document.querySelectorAll('#hud-dev-speed .speed-btn').forEach(btn => {
      const isCurrent = !GameState.paused && Number(btn.dataset.scale) === GameState.timeScale;
      btn.classList.toggle('active', isCurrent);
    });
  }

  /** Call once after player + world are hydrated */
  refresh() {
    this._updateDate();
    this._updateMoney();
    this._updateStats();
    this._updateDevControls();
    this._updateCompanyName();
    this.setLiveState('live');
  }

  _updateCompanyName() {
    if (!GameState.player) return;
    const el = document.getElementById('hud-company');
    if (el) el.textContent = GameState.player.companyName;
  }
}
